import React, { useState } from "react";
import { MoreHorizontal, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { ReportDialog } from "@/components/social/ReportDialog";
import { BlockMenuItem } from "@/components/social/BlockMenuItem";

/**
 * Three-dots overflow menu with a "Report" action (flag) for an entity
 * (personal/business/capital profile, event, resource). Renders nothing for
 * signed-out viewers or the owner. Opens the shared ReportDialog scoped to `contentType`.
 */
export function ReportMenu({
  contentType, contentId, ownerUserId, isOwner, align = "end", className = "", testId,
  blockTargetUserId, blockTargetName, blockSource, onBlockChanged,
}) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (!user) return null;
  if (isOwner) return null;
  if (ownerUserId && user.user_id === ownerUserId) return null;
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={`h-9 w-9 text-muted-foreground hover:text-foreground ${className}`}
            aria-label="More options"
            data-testid={testId || `report-menu-${contentType}`}
          >
            <MoreHorizontal className="h-5 w-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align} className="w-48">
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={() => setOpen(true)}
            data-testid={`report-${contentType}-button`}
          >
            <Flag className="mr-2 h-4 w-4" /> Report
          </DropdownMenuItem>
          {/* Platform User Blocking V1 — Block / Unblock lives beside Report but is a
              separate action: blocking never files a report. */}
          {blockTargetUserId && blockTargetUserId !== user.user_id ? (
            <BlockMenuItem
              targetUserId={blockTargetUserId}
              displayName={blockTargetName}
              source={blockSource || contentType}
              onChanged={onBlockChanged}
            />
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      <ReportDialog open={open} onOpenChange={setOpen} contentType={contentType} contentId={contentId} />
    </>
  );
}
